import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CartItem {
  id: number;
  sku: string;
  name: string;
  description?: string;
  price: number;
  image: string;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  rates_id: string | null;
  subtotal: number | null;
  total_price: number | null;
  discount_code: string | null;
  applied_promotion_code: string | null;
  discount_amount: number | null;
  discount_value: number | null;
  shipping_price: number | null;
  taxes: number | null;
  carrier: string | null;
  service_level: string | null;
  box_length_inches: number | null;
  box_width_inches: number | null;
  box_height_inches: number | null;
  box_weight_ounces: number | null;
}

const initialState: CartState = {
  items: [],
  rates_id: null,
  subtotal: null,
  total_price: null,
  discount_code: null,
  applied_promotion_code: null,
  discount_amount: null,
  discount_value: null,
  shipping_price: null,
  taxes: null,
  carrier: null,
  service_level: null,
  box_length_inches: null,
  box_width_inches: null,
  box_height_inches: null,
  box_weight_ounces: null,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<CartItem>) => {
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
      } else {
        state.items.push(action.payload);
      }
    },
    removeFromCart: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(item => item.id !== action.payload);
    },
    setOrderData: (state, action: PayloadAction<Partial<Omit<CartState, 'items'>>>) => {
      const { rates_id, shipping_price, taxes, carrier, service_level, applied_promotion_code } = action.payload;
      if (rates_id !== undefined) state.rates_id = rates_id;
      if (shipping_price !== undefined) state.shipping_price = shipping_price;
      if (taxes !== undefined) state.taxes = taxes;
      if (carrier !== undefined) state.carrier = carrier;
      if (service_level !== undefined) state.service_level = service_level;
      if (applied_promotion_code !== undefined) state.applied_promotion_code = applied_promotion_code;
    },
    setPriceData: (state, action: PayloadAction<Partial<Omit<CartState, 'items'>>>) => {
      const { subtotal, total_price, discount_code, discount_amount, discount_value } = action.payload;
      if (subtotal !== undefined) state.subtotal = subtotal;
      if (total_price !== undefined) state.total_price = total_price;
      if (discount_code !== undefined) state.discount_code = discount_code;
      if (discount_amount !== undefined) state.discount_amount = discount_amount;
      if (discount_value !== undefined) state.discount_value = discount_value;
    },
    setBoxData: (state, action: PayloadAction<Partial<Omit<CartState, 'items'>>>) => {
      const { box_length_inches, box_width_inches, box_height_inches, box_weight_ounces } = action.payload;
      if (box_length_inches !== undefined) state.box_length_inches= box_length_inches;
      if (box_width_inches!== undefined) state.box_width_inches = box_width_inches;
      if (box_height_inches !== undefined) state.box_height_inches = box_height_inches;
      if (box_weight_ounces !== undefined) state.box_weight_ounces = box_weight_ounces;
    },
    setCartData: (state, action: PayloadAction<Partial<CartState>>) => {
      return { ...state, ...action.payload };
    },
    clearCart: () => initialState,
  },
});

export const { addToCart, removeFromCart, setOrderData, setPriceData, setBoxData, setCartData, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
